import { ReactNode, createContext, FC, useEffect, useState } from 'react';

import { ApolloError, gql, useQuery } from '@apollo/client';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Application,
  Permission,
} from '../components/insights/types/InsightsTypes';
import { DocumentNode } from 'graphql';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';

type applicationQueryVariables = {
  applicationId: number;
  filter: {
    policies: string[];
  };
};

interface ApplicationQueryResult {
  app: Application;
  loading: boolean;
  error?: ApolloError;
  GetApplicationQuery: DocumentNode;
  applicationVariables: applicationQueryVariables;
  getPolicyAccess: (policyName: string) => boolean;
}

type Props = {
  children: ReactNode;
};

/**
 * SystemContext gives us a top level component we can use to pass iTRC application
 * data between components on the System page.
 */
export const SystemContext = createContext<ApplicationQueryResult>(
  {} as ApplicationQueryResult,
);

/**
 * A hook that gets application identifier
 */
export const useAppId = () => {
  const { entity } = useEntity();
  return Number(entity?.metadata?.annotations?.['comcast.net/itrc-id']);
};

/**
 * SystemProvider sets up the connection to the graph service. It allows us to  use the graph client where needed in the application.
 * @param props Props of the context
 * @param props.children - The children of this context.
 */
export const SystemProvider: FC<Props> = (props: Props) => {
  const appId = useAppId();
  const [app, setApp] = useState<Application>({} as Application);
  const isPrivacySurveyEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.PRIVACY_SURVEY,
  );

  const applicationVariables: applicationQueryVariables = {
    applicationId: Number(appId),
    filter: {
      policies: [
        'UpdateApplicationPolicy',
        'UpdateTagPolicy',
        'ManageCppTypesPolicy',
        'FunctionalAreaEditConnectionPolicy',
        'AccessAuditEventsPolicy',
        'RetireApplicationPolicy',
        'ManageApplicationTagsPolicy',
        'ShowbackTabPolicy',
      ],
    },
  };

  const GetApplicationQuery = gql`
  query GetApplicationDetails(
    $applicationId: Int!
    $filter: AuthorizationPolicyFilter!
  ) {
    resources {
      application(applicationId: $applicationId) {
        applicationId
        name
        description
        status
        appGroup
        greenEnergyProfile {
          score
          metrics {
            calculatedPointValue
            metricName
            unitDescription
            unitMultiplier
            unitQuantity
          }
        }
        serviceLevelName
        serviceLevel
        serviceDomain
        createdAt
        updatedAt
        corporateEntity
        systemType
        createdBy {
          firstName
          lastName
        }
        iopOwner
        iopSupportGroup
        informationClassificationPolicy {
          name
        }
        ${
          ConfigHelper.instance.isFeatureEnabled(
            Feature.BUSINESS_CAPABILITIES,
            false,
          )
            ? `iopBusinessCapabilities {
          sysId
          name
          hierarchyId
        }`
            : ''
        }
        iopServiceOfferings {
          number
          name
          operationalStatus
          shortDescription
        }
        iopServiceBusinessItems {
          number
          name
          serviceStatus
          shortDescription
          state
        }
        customAttributes {
          key
          value
        }
        url
        owner {
          firstName
          lastName
          email
          accountEnabled
        }
        tags {
          tag
          id
        }
        sdlTeamsPaged {
          totalCount
          teams {
            sdlTeamName
          }
        }
        functionalAreaConnection {
          dataBusinessOwner {
            firstName
            lastName
            login
            email
          }
          dataLeads {
            firstName
            lastName
            login
            email
          }
          id
          name
        }
        ${isPrivacySurveyEnabled ? 'privacy { systemType consumerType }' : ''}
        authorizationPolicy(filter: $filter) {
          policy
          hasAccess
          data
        }
        corporateEntity
      }
    }
  }
`;

  const { loading, error, data } = useQuery(GetApplicationQuery, {
    errorPolicy: 'all',
    variables: applicationVariables,
  });

  const getPolicyAccess = (policyName: string): boolean => {
    // Finds specific policy when there is no additional context required
    const findPolicy = data?.resources?.application?.authorizationPolicy?.find(
      (permission: Permission) => permission?.policy === policyName,
    );

    return findPolicy?.hasAccess ?? false;
  };

  useEffect(() => {
    setApp(data?.resources?.application);
  }, [data]);

  return (
    <SystemContext.Provider
      value={{
        app,
        loading,
        error,
        GetApplicationQuery,
        applicationVariables,
        getPolicyAccess,
      }}
    >
      {props.children}
    </SystemContext.Provider>
  );
};

export default SystemProvider;
